/**
 * KAN-811: блок входа менеджера в карточке менеджера (страница показывает
 * его только владельцу). KAN-846: "Логин и пароль" рядом со "Ссылкой для
 * входа" (ManagerAppBlock). Логин - почта или телефон менеджера: почта
 * необязательна, если у менеджера указан телефон.
 *   - входа нет: форма Почта + Пароль и "Создать вход";
 *   - вход через приложение (app_only, по ссылке, без пароля): "Вход через
 *     приложение", та же форма и "Задать логин и пароль";
 *   - вход с паролем: логин, статус, "Сменить пароль", "Отключить вход" / "Включить вход".
 * Действия и ошибки сервера в хуке useManagerAccount.
 */
import { useId, useState } from 'react';
import { KeyRound } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useManagerAccount } from '@/hooks/useManagerAccount';
import type { ManagerAccount, ManagerAccountCreateBody } from '@/types/manager';
import { ManagerPasswordDialog } from './ManagerPasswordDialog';

interface Props {
  managerId: string;
  account: ManagerAccount | null | undefined;
  /** Телефон менеджера из карточки: при нём почта необязательна (логин - телефон). */
  phone?: string;
}

export const APP_ONLY_ACCOUNT_LABEL = 'Вход через приложение';
export const ACCOUNT_BLOCK_TITLE = 'Логин и пароль';
export const APP_ONLY_HINT = 'Пароля нет, менеджер входит по ссылке. Задайте логин и пароль, чтобы он мог войти без неё.';

export function ManagerAccountBlock({ managerId, account, phone = '' }: Props): JSX.Element {
  const { create, changePassword, setEnabled, error, passwordError } = useManagerAccount(managerId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const appOnly = !!account?.app_only;

  return (
    <Card data-testid="manager-account-block">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4 text-gray-400" aria-hidden="true" />
          {ACCOUNT_BLOCK_TITLE}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {account && (
          <>
            <div className="space-y-1 text-sm">
              <p className="font-medium text-gray-900" data-testid="manager-account-login">
                {appOnly ? APP_ONLY_ACCOUNT_LABEL : account.login || account.email}
              </p>
              <p
                data-testid="manager-account-status"
                className={account.is_active ? 'text-green-700' : 'text-gray-500'}
              >
                {account.is_active ? 'Вход включён' : 'Вход отключён'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {!appOnly && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    changePassword.reset();
                    setDialogOpen(true);
                  }}
                >
                  Сменить пароль
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                disabled={setEnabled.isPending}
                onClick={() => setEnabled.mutate(!account.is_active)}
              >
                {account.is_active ? 'Отключить вход' : 'Включить вход'}
              </Button>
            </div>
            <ManagerPasswordDialog
              open={dialogOpen}
              pending={changePassword.isPending}
              error={passwordError}
              onClose={() => setDialogOpen(false)}
              onSave={(password) =>
                changePassword.mutate(password, { onSuccess: () => setDialogOpen(false) })
              }
            />
          </>
        )}
        {(!account || appOnly) && (
          <CredentialsForm
            phone={phone.trim()}
            submitLabel={appOnly ? 'Задать логин и пароль' : 'Создать вход'}
            intro={appOnly ? APP_ONLY_HINT : null}
            pending={create.isPending}
            onSubmit={(body) => create.mutate(body)}
          />
        )}
        {error && (
          <p className="text-sm text-red-700" role="alert" data-testid="manager-account-error">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

interface CredentialsFormProps {
  phone: string;
  submitLabel: string;
  intro: string | null;
  pending: boolean;
  onSubmit: (body: ManagerAccountCreateBody) => void;
}

function CredentialsForm({ phone, submitLabel, intro, pending, onSubmit }: CredentialsFormProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const emailId = useId();
  const passwordId = useId();
  const trimmed = email.trim();
  const canSubmit = !!password && (!!trimmed || !!phone);

  return (
    <form
      className="grid gap-3 sm:max-w-md"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit(trimmed ? { email: trimmed, password } : { password });
      }}
    >
      {intro && <p className="text-sm text-gray-600">{intro}</p>}
      <p className="text-sm text-gray-600" data-testid="manager-account-login-hint">
        {phone
          ? `Логин: телефон ${phone}. Можно добавить почту, тогда войти можно и по ней.`
          : 'Логин: почта. Если указать телефон в карточке менеджера, почта не нужна.'}
      </p>
      <div className="space-y-1">
        <Label htmlFor={emailId}>{phone ? 'Почта (необязательно)' : 'Почта'}</Label>
        <Input
          id={emailId}
          type="email"
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={passwordId}>Пароль</Label>
        <Input
          id={passwordId}
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <div>
        <Button type="submit" disabled={!canSubmit || pending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
